import { useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { authClient, signInWithTelegram } from "@/lib/auth";
import { cn } from "@shared/lib/utils";

type AuthMode = "login" | "signup";

type AuthModalProps = {
  open: boolean;
  onClose: () => void;
  initialMode: AuthMode;
};

const FIELD_LABEL =
  "font-mono text-[9px] font-medium uppercase tracking-[0.16em] text-ink-mute";
const FIELD_INPUT =
  "h-11 w-full rounded-lg border border-line bg-white/4 px-3 text-sm text-ink outline-none transition-colors duration-200 placeholder:text-ink-mute focus:border-accent/60 disabled:opacity-60";

/**
 * Email/password auth modal (login ↔ signup tabs) + Telegram OAuth entry.
 * 모달 규약(F-26): 포털 + 스크롤락 + ESC 닫기 + 탭 포커스 트랩 (BuybackModal
 * 패턴). z-[80] — 모바일 드로어(z-70) 위에서도 열린다.
 * 성공 시 window.location.reload(): useSession은 자동 갱신되지만(better-auth
 * /sign-in/email 세션 시그널) 익명 상태로 채워진 페이지 데이터 캐시는 아니라서,
 * 텔레그램 리다이렉트 복귀와 동일하게 전체 리로드로 맞춘다.
 * KYC 단계는 추후 — 여기서는 계정 생성/로그인까지만.
 */
export function AuthModal({ open, onClose, initialMode }: AuthModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 열릴 때마다 요청 모드부터, 이전 오류·진행 상태는 리셋.
  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setPending(false);
      setError(null);
    }
  }, [open, initialMode]);

  // 스크롤락 + ESC (제출 중엔 ESC 무시)
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !pending) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, pending, onClose]);

  // 포커스 트랩 — 열릴 때 첫 입력으로, 닫히면 트리거로 복귀
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLElement>("input")?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.tabIndex >= 0);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !dialog.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !dialog.contains(active)) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      restoreFocusRef.current?.focus();
    };
  }, [open, mode]);

  if (!open) return null;

  const switchMode = (next: AuthMode) => {
    setMode(next);
    setError(null);
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending) return;
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") ?? "").trim();
    const password = String(form.get("password") ?? "");
    const name = String(form.get("name") ?? "").trim();
    // better-auth 기본 정책(minPasswordLength 8) 선검증 — 서버 왕복 없이 즉시.
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setPending(true);
    setError(null);
    void (async () => {
      const { error: authError } =
        mode === "login"
          ? await authClient.signIn.email({ email, password })
          : await authClient.signUp.email({ email, password, name });
      if (authError) {
        setError(authError.message || (mode === "login" ? "Sign in failed" : "Sign up failed"));
        setPending(false);
        return;
      }
      onClose();
      window.location.reload();
    })();
  };

  const telegram = () => {
    if (pending) return;
    setPending(true);
    setError(null);
    signInWithTelegram(window.location.pathname).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : "Telegram sign-in failed");
      setPending(false);
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end justify-center sm:items-center">
      <div
        className="absolute inset-0 bg-black/65"
        aria-hidden
        onClick={pending ? undefined : onClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={mode === "login" ? "Log in" : "Sign up"}
        className="relative z-10 w-full max-w-md rounded-t-2xl border border-line bg-(--ty-bg-2) p-5 sm:rounded-2xl"
      >
        <div role="tablist" aria-label="Authentication mode" className="flex border-b border-line">
          {(
            [
              ["login", "Log in"],
              ["signup", "Sign up"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              id={`auth-tab-${value}`}
              aria-selected={mode === value}
              aria-controls="auth-panel"
              disabled={pending}
              onClick={() => switchMode(value)}
              className={cn(
                "-mb-px min-h-11 flex-1 border-b font-sans text-sm font-semibold outline-none transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70",
                mode === value
                  ? "border-accent text-ink"
                  : "border-transparent text-ink-mute hover:text-ink",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <form
          id="auth-panel"
          role="tabpanel"
          aria-labelledby={`auth-tab-${mode}`}
          onSubmit={submit}
          className="mt-4 flex flex-col gap-3"
        >
          {mode === "signup" ? (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="auth-name" className={FIELD_LABEL}>
                Display name
              </label>
              <input
                id="auth-name"
                name="name"
                type="text"
                required
                autoComplete="name"
                disabled={pending}
                className={FIELD_INPUT}
              />
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <label htmlFor="auth-email" className={FIELD_LABEL}>
              Email
            </label>
            <input
              id="auth-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              disabled={pending}
              className={FIELD_INPUT}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="auth-password" className={FIELD_LABEL}>
              Password
            </label>
            <input
              id="auth-password"
              name="password"
              type="password"
              required
              minLength={8}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              disabled={pending}
              className={FIELD_INPUT}
            />
          </div>

          {error ? (
            <p className="font-mono text-[11px] leading-snug text-danger">{error}</p>
          ) : null}

          {/* GlowButton은 type="submit" 미지원 — 동일 cta/md 클래스의 네이티브 submit. */}
          <button
            type="submit"
            disabled={pending}
            className="ty-cta inline-flex min-h-11 w-full items-center justify-center rounded-(--ty-radius-btn) px-5 py-2.5 font-sans text-sm font-semibold outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70 disabled:pointer-events-none disabled:opacity-50"
          >
            {pending
              ? mode === "login"
                ? "Logging in…"
                : "Signing up…"
              : mode === "login"
                ? "Log in"
                : "Sign up"}
          </button>
        </form>

        <div className="my-4 flex items-center gap-3" aria-hidden>
          <span className="h-px flex-1 bg-line" />
          <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-ink-mute">or</span>
          <span className="h-px flex-1 bg-line" />
        </div>

        {/* GlowButton ghost/cta와 동일 문법 — w-fit 기본과의 충돌 없이 풀폭. */}
        <button
          type="button"
          onClick={telegram}
          disabled={pending}
          className="inline-flex min-h-11 w-full items-center justify-center rounded-(--ty-radius-btn) border border-line px-5 py-2.5 font-sans text-sm font-semibold text-ink-dim outline-none transition-colors duration-200 hover:border-accent/60 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/70 disabled:pointer-events-none disabled:opacity-50"
        >
          Continue with Telegram
        </button>
      </div>
    </div>,
    document.body,
  );
}
